'use strict';

import { TUPLE_CAP_MOBILE } from '../../core/constants.js';
import { displayOf, foldedDisplayOf } from '../norm.js';
import { parseUmiaqQuery, matchPattern, findTuples, variableColors, variableHighlights, caseOK } from '../umiaq.js';

// Memory ceiling, not a UX cap: the worker retains every streamed tuple for
// scrollback, so an unbounded broad query would blow memory. Defaults to the
// conservative mobile cap, raised on desktop at boot. The default must be the SAFE
// one: the worker can't detect mobile itself (no window.matchMedia), so a missed
// boot message has to under-retain, not over-retain into an iOS reload.
let tupleMaxResults = TUPLE_CAP_MOBILE;

export function configureUmiaq({ maxResults } = {}) {
  if (Number.isFinite(maxResults) && maxResults > 0) tupleMaxResults = maxResults;
}

export default {
  name: 'Umiaq', icon: '🛶', category: 'search',
  desc: 'Advanced search with variables and constraints',
  example: 'ABBA · AB;BA',
  params: [
    { key: 'query', placeholder: 'AB;BA', assist: 'umiaq', raw: true },
  ],
  // Arity is read off the query, not a toggle: one binding filters (arity 1),
  // several bindings find tuples (arity = binding count).
  kind(params) {
    const parsed = parseUmiaqQuery(params?.query || '');
    return parsed.ok && parsed.arity > 1 ? 'tuple' : 'filter';
  },
  // Filter-mode only: lights a single binding's matched variables. Drop it and that
  // silently stops; the tuple path (other kind) colors its own lanes, ignoring this.
  input: 'highlight', output: 'plain',
  matchOn: 'both',
  isInert: params => !parseUmiaqQuery(params?.query || '').ok,
  error(params) {
    const parsed = parseUmiaqQuery(params?.query || '');
    return parsed.ok || parsed.empty ? null : parsed.error;
  },
  prepare(params) {
    const parsed = parseUmiaqQuery(params.query || '');
    if (!parsed.ok) return null;
    parsed.varColor = variableColors(parsed.variables);
    return parsed;
  },
  run(wlEntry, parsed) {
    if (!parsed) return true;
    const disp = parsed.arm === 'display';
    const word = disp ? foldedDisplayOf(wlEntry) : wlEntry.norm;
    const binding = parsed.bindings[0];
    const matches = matchPattern(word, binding, parsed.constraints);
    const m = parsed.caseSensitive ? matches.find(a => caseOK(displayOf(wlEntry), binding, a)) : matches[0];
    if (!m) return false;
    return variableHighlights(word, binding, m, parsed.varColor, disp ? 'display' : null);
  },
  findTuples(pool, parsed, ctx) {
    return findTuples(parsed, pool, { numResults: tupleMaxResults, onBatch: ctx.onBatch, y: ctx.y, signal: ctx.signal });
  },
};
